/**
 * Mermaid ER diagram parser.
 * Falls back to regex parsing if mermaid.js API is unavailable.
 */

import type { IRGraph, IRNode, IREdge, Direction } from '../types';
import { NodeShape, createEmptyGraph } from '../types';
import { SHAPE_REGISTRY } from '../registry';

export interface ErAttribute {
  name: string;
  type: string;
  key?: 'PK' | 'FK' | 'UK';
  comment?: string;
}

let mermaidInstance: typeof import('mermaid')['default'] | null = null;

async function getMermaid() {
  if (!mermaidInstance) {
    const mod = await import('mermaid');
    mermaidInstance = mod.default;
    mermaidInstance.initialize({ startOnLoad: false, logLevel: 5 as any });
  }
  return mermaidInstance;
}

export async function parseMermaidEr(text: string): Promise<IRGraph> {
  try {
    return await parseMermaidApi(text);
  } catch {
    return parseRegex(text);
  }
}

async function parseMermaidApi(text: string): Promise<IRGraph> {
  const mermaid = await getMermaid();
  const diagram = await mermaid.mermaidAPI.getDiagramFromText(text);
  const db = (diagram as any).db;

  const graph = createEmptyGraph('LR', 'er');

  const entities: Map<string, any> = db.getEntities?.() ?? new Map();
  const relationships: any[] = db.getRelationships?.() ?? [];

  for (const [id, entity] of entities) {
    const attributes: ErAttribute[] = (entity.attributes ?? []).map((attr: any) => ({
      name: attr.attributeName ?? attr.name ?? '',
      type: attr.attributeType ?? attr.type ?? '',
      key: attr.attributeKeyType ?? attr.attributeKeyTypeList?.[0] ?? attr.keyType ?? attr.key ?? undefined,
      comment: attr.attributeComment ?? attr.comment ?? undefined,
    }));

    const displayLabel = entity.alias || id;
    const attrCount = attributes.length;
    const longestAttr = Math.max(
      displayLabel.length,
      ...attributes.map((a) => `${a.type} ${a.name} ${a.key ?? ''}`.length),
      1,
    );
    const width = Math.max(SHAPE_REGISTRY[NodeShape.ErEntity].defaultWidth, longestAttr * 9 + 60);
    const height = 30 + attrCount * 22 + 10;

    const node: IRNode = {
      id,
      label: displayLabel,
      shape: NodeShape.ErEntity,
      width,
      height,
      meta: { attributes },
    };
    graph.nodes.set(id, node);
  }

  // If API returned no entities, fall back
  if (graph.nodes.size === 0) {
    return parseRegex(text);
  }

  let edgeCounter = 0;
  for (const rel of relationships) {
    edgeCounter++;
    const cardinality = parseRelationSpec(rel);
    const isDashed = rel.relSpec?.relType === 'NON_IDENTIFYING';
    const edge: IREdge = {
      id: `e${edgeCounter}`,
      source: rel.entityA,
      target: rel.entityB,
      label: rel.roleA || undefined,
      dashed: isDashed || undefined,
      meta: { cardinality },
    };
    graph.edges.push(edge);
  }

  return graph;
}

function parseRelationSpec(rel: any): { from: string; to: string } {
  // mermaid stores cardinality in relSpec
  const relSpec = rel.relSpec ?? {};
  return {
    from: cardinalityToString(relSpec.cardA ?? 'ZERO_OR_MORE'),
    to: cardinalityToString(relSpec.cardB ?? 'ZERO_OR_MORE'),
  };
}

function cardinalityToString(card: string): string {
  switch (card) {
    case 'ONLY_ONE':
    case 'ONE_OR_MORE':
      return card === 'ONLY_ONE' ? '||' : '|{';
    case 'ZERO_OR_ONE':
      return 'o|';
    case 'ZERO_OR_MORE':
      return 'o{';
    default:
      return card;
  }
}

// ─── Regex fallback ──────────────────────────────────────────────

// Entity block: ENTITY_NAME["Alias"] { ... }
const ENTITY_BLOCK_RE = /(\w+)(?:\s*\["([^"]+)"\])?\s*\{([^}]*)\}/gs;
// Attribute line: type name PK/FK/UK "comment"
const ATTR_RE = /^\s*(\w+)\s+(\w+)\s*(PK|FK|UK)?\s*(?:"([^"]*)")?\s*$/;
// Relationship: ENTITY1 ||--o{ ENTITY2 : "label" (-- identifying, .. non-identifying)
const RELATIONSHIP_RE =
  /(\w+)\s+(\|[{|o]|[{}|o]\|?|[{}o]{2})(-{2}|\.{2})(\|[{|o]|[{}|o]\|?|[{}o]{2})\s+(\w+)\s*:\s*"?([^"\n]*)"?/g;

function parseRegex(text: string): IRGraph {
  const graph = createEmptyGraph('LR', 'er');

  // Parse entity blocks
  ENTITY_BLOCK_RE.lastIndex = 0;
  let em: RegExpExecArray | null;
  while ((em = ENTITY_BLOCK_RE.exec(text)) !== null) {
    const entityName = em[1];
    if (entityName === 'erDiagram') continue;
    const alias = em[2] || undefined;
    const body = em[3];
    const attributes: ErAttribute[] = [];

    for (const line of body.split('\n')) {
      const match = ATTR_RE.exec(line.trim());
      if (match) {
        attributes.push({
          type: match[1],
          name: match[2],
          key: (match[3] as 'PK' | 'FK' | 'UK') || undefined,
          comment: match[4] || undefined,
        });
      }
    }

    const displayLabel = alias || entityName;
    const attrCount = attributes.length;
    const longestAttr = Math.max(
      displayLabel.length,
      ...attributes.map((a) => `${a.type} ${a.name} ${a.key ?? ''}`.length),
      1,
    );
    const width = Math.max(SHAPE_REGISTRY[NodeShape.ErEntity].defaultWidth, longestAttr * 9 + 60);
    const height = 30 + attrCount * 22 + 10;

    graph.nodes.set(entityName, {
      id: entityName,
      label: displayLabel,
      shape: NodeShape.ErEntity,
      width,
      height,
      meta: { attributes },
    });
  }

  // Parse relationships
  RELATIONSHIP_RE.lastIndex = 0;
  let rm: RegExpExecArray | null;
  while ((rm = RELATIONSHIP_RE.exec(text)) !== null) {
    const entityA = rm[1];
    const cardA = rm[2];
    const separator = rm[3]; // '--' or '..'
    const cardB = rm[4];
    const entityB = rm[5];
    const label = rm[6]?.trim() || undefined;

    // Ensure entities exist even without attribute blocks
    ensureEntityNode(graph, entityA);
    ensureEntityNode(graph, entityB);

    graph.edges.push({
      id: `e${graph.edges.length + 1}`,
      source: entityA,
      target: entityB,
      label,
      dashed: separator === '..' || undefined,
      meta: { cardinality: { from: cardA, to: cardB } },
    });
  }

  return graph;
}

function ensureEntityNode(graph: IRGraph, id: string): void {
  if (graph.nodes.has(id)) return;
  const entry = SHAPE_REGISTRY[NodeShape.ErEntity];
  graph.nodes.set(id, {
    id,
    label: id,
    shape: NodeShape.ErEntity,
    width: entry.defaultWidth,
    height: entry.defaultHeight,
    meta: { attributes: [] },
  });
}
